import { yupResolver } from '@hookform/resolvers/yup';
import { formatDateISO, parseDateISO } from '@zorro/shared/formatters';
import {
  useFlowId,
  useIdempotencyKey,
  useMonolithMutation,
  useMonolithQuery,
} from '@zorro/web/ui-utils';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import {
  CoveragePeriodFormFields,
  coveragePeriodSchema,
} from './CoveragePeriodFormUtils';

type Props = {
  onboardingPeriodId: string;
  employeeId: string;
  shouldShowLoadingOverlay?: boolean;
};

export const useCoveragePeriodForm = ({
  onboardingPeriodId,
  employeeId,
  shouldShowLoadingOverlay = true,
}: Props) => {
  const idempotencyKey = useIdempotencyKey();
  const flowId = useFlowId();

  const { isLoading, data: onboardingPeriod } = useMonolithQuery({
    method: 'onboardingPeriodsControllerFindOne',
    params: [employeeId, onboardingPeriodId],
  });

  const { tryMutate: mutateOnboardingPeriodEffectiveDates } =
    useMonolithMutation({
      method: 'onboardingPeriodsControllerUpdateEffectiveDates',
      shouldReloadPage: false,
      shouldShowLoadingOverlay,
    });

  const coveragePeriodForm = useForm<CoveragePeriodFormFields>({
    mode: 'all',
    defaultValues: {
      effectiveFrom: onboardingPeriod
        ? parseDateISO(onboardingPeriod.effectiveFrom).toDate()
        : undefined,
      effectiveUntil: onboardingPeriod
        ? parseDateISO(onboardingPeriod.effectiveUntil).toDate()
        : undefined,
    },
    resolver: yupResolver(coveragePeriodSchema),
  });

  useEffect(() => {
    if (onboardingPeriod?.effectiveFrom && onboardingPeriod?.effectiveUntil) {
      coveragePeriodForm.reset({
        effectiveFrom: parseDateISO(onboardingPeriod.effectiveFrom).toDate(),
        effectiveUntil: parseDateISO(onboardingPeriod.effectiveUntil).toDate(),
      });
    }
  }, [
    onboardingPeriodId,
    onboardingPeriod?.effectiveFrom,
    onboardingPeriod?.effectiveUntil,
  ]);

  return {
    isLoading,
    coveragePeriodForm,
    onboardingPeriod,
    onSubmit: async () => {
      const { effectiveFrom, effectiveUntil } = coveragePeriodForm.getValues();

      if (!effectiveFrom || !effectiveUntil) {
        return false;
      }

      const mutationResult = await mutateOnboardingPeriodEffectiveDates(
        [
          employeeId,
          onboardingPeriodId,
          idempotencyKey.get(),
          {
            from: formatDateISO(effectiveFrom),
            until: formatDateISO(effectiveUntil),
          },
        ],
        { flowId }
      );

      return mutationResult.isOk();
    },
  };
};
